import { useEffect, useState } from "react"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"
import type { DestructionActStatus, OtkDestructionAct } from "shared"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DataTable } from "@/components/DataTable"
import { DateRangePicker } from "@/components/DateRangePicker"
import { ActViewDialog } from "@/components/warehouse/ActViewDialog"
import { buildActColumns } from "@/components/warehouse/actColumns"
import { getDestructionActsPage } from "@/lib/api/destructionActs"

// The list endpoint filters by one status at a time (default `ACCEPTED`). The
// second tab is the only place a `SUBMISSION_PENDING` act stays reachable
// after its sign dialog closes — its blanks are claimed, so they leave the
// destruction selection, and they reach «Знищені» only after acceptance.
const statusTabs: { value: DestructionActStatus; label: string }[] = [
  { value: "ACCEPTED", label: "Прийняті" },
  { value: "SUBMISSION_PENDING", label: "Очікують відправлення" },
]

interface ActsListState {
  /** The `status|actDateFrom|actDateTo` filter this data was fetched for — lets the render below treat a stale bucket from a previous filter as "loading" without ever setting state synchronously inside the fetch effect. */
  key: string
  items: OtkDestructionAct[]
  cursor: string | null
  hasMore: boolean
  error: string | null
}

const EMPTY_STATE: ActsListState = {
  key: "",
  items: [],
  cursor: null,
  hasMore: false,
  error: null,
}

/**
 * warehouse-acts.md's `/warehouse/acts` — the read-only historical archive
 * of destruction acts, real data via `GET /otk/destruction-acts`. Acts are
 * composed elsewhere (`/warehouse/blanks`'s `DestructionActDialog`); the
 * only write reachable from here is `ActViewDialog`'s manual retry of a
 * `SUBMISSION_PENDING` act (customer decision 30.09.2026). Unlike the wireframe's own local-only acts
 * array, the date-range filter is applied server-side (`actDateFrom`/
 * `actDateTo` query params), so there is no client-side date filtering to
 * reconcile with the "Немає сформованих актів" display-only toggle the
 * wireframe also had — that toggle had no real backend counterpart to begin
 * with and isn't ported.
 */
export function WarehouseActs() {
  const [status, setStatus] = useState<DestructionActStatus>("ACCEPTED")
  const [actDateRange, setActDateRange] = useState<DateRange | undefined>()
  const [state, setState] = useState<ActsListState>(EMPTY_STATE)
  const [viewedActId, setViewedActId] = useState<string | null>(null)

  const actDateFrom = actDateRange?.from ? format(actDateRange.from, "yyyy-MM-dd") : undefined
  const actDateTo = actDateRange?.to
    ? format(actDateRange.to, "yyyy-MM-dd")
    : actDateRange?.from
      ? actDateFrom
      : undefined
  const filterKey = `${status}|${actDateFrom ?? ""}|${actDateTo ?? ""}`
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  useEffect(() => {
    let cancelled = false
    getDestructionActsPage({ status, actDateFrom, actDateTo })
      .then((page) => {
        if (cancelled) return
        setState({
          key: filterKey,
          items: page.data,
          cursor: page.nextPage,
          hasMore: page.nextPage !== null,
          error: null,
        })
      })
      .catch(() => {
        if (cancelled) return
        setState({
          key: filterKey,
          items: [],
          cursor: null,
          hasMore: false,
          error: "Не вдалося завантажити акти знищення.",
        })
      })
    return () => {
      cancelled = true
    }
  }, [filterKey, status, actDateFrom, actDateTo])

  // Data from a previous filter reads as still-loading until its own fetch
  // above settles and replaces `state` with a bucket keyed to `filterKey`.
  const isLoading = state.key !== filterKey
  const items = isLoading ? [] : state.items
  const error = isLoading ? null : state.error

  async function loadMore() {
    if (isLoading || !state.hasMore || isLoadingMore || !state.cursor) return
    setIsLoadingMore(true)
    try {
      const page = await getDestructionActsPage({
        status,
        actDateFrom,
        actDateTo,
        cursor: state.cursor,
      })
      setState((s) => ({
        ...s,
        items: [...s.items, ...page.data],
        cursor: page.nextPage,
        hasMore: page.nextPage !== null,
      }))
    } catch {
      setState((s) => ({ ...s, error: "Не вдалося завантажити акти знищення." }))
    } finally {
      setIsLoadingMore(false)
    }
  }

  const columns = buildActColumns(setViewedActId)

  // A retried act that got accepted leaves the «Очікують відправлення» tab.
  function handleActChanged(act: OtkDestructionAct) {
    setState((s) => ({
      ...s,
      items: s.items.flatMap((a) =>
        a.id !== act.id ? [a] : act.status === status ? [act] : []
      ),
    }))
  }

  return (
    <>
      <div className="print:hidden">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Акти знищення</h1>
          <p className="text-muted-foreground">
            Історичний архів сформованих актів знищення спецпродукції.
          </p>
        </div>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Сформовані акти</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <Tabs
                onValueChange={(value) => setStatus(value as DestructionActStatus)}
                value={status}
              >
                <TabsList className="!h-10 justify-start">
                  {statusTabs.map((t) => (
                    <TabsTrigger className="px-4 text-sm" key={t.value} value={t.value}>
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <DateRangePicker
                onChange={setActDateRange}
                placeholder="Дата акту"
                value={actDateRange}
              />
            </div>
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : !isLoading && items.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                {actDateRange?.from
                  ? "Актів за обраним періодом не знайдено"
                  : status === "SUBMISSION_PENDING"
                    ? "Немає актів, що очікують відправлення"
                    : "Акти знищення відсутні"}
              </p>
            ) : (
              <DataTable
                columns={columns}
                data={items}
                defaultSorting={[{ id: "actDate", desc: true }]}
                enablePagination={false}
                footer={
                  !isLoading &&
                  state.hasMore && (
                    <div className="flex justify-center border-t p-3">
                      <Button
                        disabled={isLoadingMore}
                        onClick={() => void loadMore()}
                        variant="outline"
                      >
                        {isLoadingMore ? "Завантаження…" : "Завантажити ще"}
                      </Button>
                    </div>
                  )
                }
                getRowId={(a) => a.id}
                isLoading={isLoading}
                tableClassName="table-auto"
              />
            )}
          </CardContent>
        </Card>
      </div>
      <ActViewDialog
        actId={viewedActId}
        onActChanged={handleActChanged}
        onOpenChange={(open) => !open && setViewedActId(null)}
      />
    </>
  )
}
